import React from 'react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Brain,
  RefreshCw,
  Loader2,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertCircle,
  CheckCircle,
  Target,
  MessageSquare,
  ListChecks
} from 'lucide-react';

function SentimentBadge({ sentiment }) {
  const config = {
    positive: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20', icon: TrendingUp },
    neutral: { bg: 'bg-slate-500/10', text: 'text-slate-400', border: 'border-slate-500/20', icon: Minus },
    negative: { bg: 'bg-rose-500/10', text: 'text-rose-400', border: 'border-rose-500/20', icon: TrendingDown },
  };
  const c = config[sentiment?.toLowerCase()] || config.neutral;
  const Icon = c.icon;

  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border ${c.bg} ${c.text} ${c.border}`}>
      <Icon className="w-3.5 h-3.5" />
      {sentiment || 'Unknown'}
    </span>
  );
}

function InterestLevel({ level }) {
  const numLevel = parseInt(level) || 0;
  const color = numLevel >= 7 ? 'bg-emerald-500' : numLevel >= 4 ? 'bg-amber-500' : 'bg-rose-500';

  return (
    <div className="space-y-1">
      <div className="flex justify-between items-center">
        <span className="text-xs text-gray-500 uppercase tracking-wider">Interest Level</span>
        <span className="font-mono text-sm text-gray-900">{numLevel}/10</span>
      </div>
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${numLevel * 10}%` }} />
      </div>
    </div>
  );
}

export default function CallAnalysisSection({ call, onReanalyze }) {
  const analysis = call.analysis_result;
  const status = call.analysis_status;

  if (!status && !analysis) return null;

  // Pending or running
  if (status === 'pending' || status === 'running') {
    return (
      <div className="p-4 rounded-lg bg-gray-50 border border-gray-200">
        <div className="flex items-center gap-3">
          <Brain className="w-4 h-4 text-blue-400" />
          <span className="text-sm font-medium text-gray-700">Call Analysis</span>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border bg-blue-500/10 text-blue-400 border-blue-500/20">
            <Loader2 className="w-3 h-3 animate-spin" />
            {status === 'running' ? 'Analyzing...' : 'Analysis Pending'}
          </span>
        </div>
        <p className="text-sm text-gray-500 mt-2">Analysis is being processed. This usually takes 10-30 seconds.</p>
      </div>
    );
  }

  // Failed
  if (status === 'failed') {
    return (
      <div className="p-4 rounded-lg bg-gray-50 border border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Brain className="w-4 h-4 text-rose-400" />
            <span className="text-sm font-medium text-gray-700">Call Analysis</span>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border bg-rose-500/10 text-rose-400 border-rose-500/20">
              <AlertCircle className="w-3 h-3" />
              Analysis Failed
            </span>
          </div>
          {onReanalyze && (
            <Button size="sm" variant="ghost" className="text-gray-500 hover:text-gray-900" onClick={onReanalyze}>
              <RefreshCw className="w-3 h-3 mr-1" /> Retry
            </Button>
          )}
        </div>
        <p className="text-sm text-gray-500 mt-2">
          {analysis?._meta?.error || 'Analysis failed. Click retry to try again.'}
        </p>
      </div>
    );
  }

  // Completed
  if (!analysis) return null;

  // Normalize field names — handle both old and new analysis prompt formats
  const get = (...keys) => {
    for (const k of keys) {
      if (analysis[k] !== undefined && analysis[k] !== null && analysis[k] !== '') return analysis[k];
    }
    return undefined;
  };

  // Ensure a value is an array (old prompts sometimes return strings)
  const toArray = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    if (typeof val === 'string') return [val];
    return [];
  };

  const sentiment = get('sentiment');
  const effectiveInterest = get('customer_interest_level', 'interest_level');
  const intent = get('intent', 'buyer_intent', 'customer_intent', 'caller_intent');
  const next_action = get('next_action', 'next_steps', 'recommended_action', 'follow_up');
  const summary = get('summary', 'call_summary');
  const outcome = get('outcome', 'call_outcome', 'result');
  const key_topics = toArray(get('key_topics', 'topics'));
  const key_objections = toArray(get('key_objections', 'objections'));
  const action_items = toArray(get('action_items', 'follow_up_actions'));
  const { _meta } = analysis;

  // Known keys to exclude from "Additional Data"
  const knownKeys = new Set([
    'sentiment', 'customer_interest_level', 'interest_level',
    'intent', 'buyer_intent', 'customer_intent', 'caller_intent',
    'next_action', 'next_steps', 'recommended_action', 'follow_up',
    'summary', 'call_summary',
    'outcome', 'call_outcome', 'result',
    'key_topics', 'topics',
    'key_objections', 'objections',
    'action_items', 'follow_up_actions',
    '_meta'
  ]);
  const otherFields = Object.fromEntries(
    Object.entries(analysis).filter(([k]) => !knownKeys.has(k))
  );

  // Convert objects to display strings for intent/next_action (old prompts sometimes return objects)
  const intentStr = intent ? (typeof intent === 'object' ? (intent.action || intent.label || JSON.stringify(intent)) : String(intent)) : null;
  const nextActionStr = next_action ? (typeof next_action === 'object' ? (next_action.action || next_action.label || JSON.stringify(next_action)) : String(next_action)) : null;

  return (
    <div className="p-4 rounded-lg bg-gray-50 border border-gray-200 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Brain className="w-4 h-4 text-blue-400" />
          <span className="text-sm font-medium text-gray-700">Call Analysis</span>
        </div>
        <div className="flex items-center gap-2">
          {_meta?.analyzed_at && (
            <span className="text-xs text-gray-500">{new Date(_meta.analyzed_at).toLocaleString()}</span>
          )}
          {onReanalyze && (
            <Button size="sm" variant="ghost" className="text-gray-500 hover:text-gray-900" onClick={onReanalyze}>
              <RefreshCw className="w-3 h-3 mr-1" /> Re-analyze
            </Button>
          )}
        </div>
      </div>

      {/* Top row: Sentiment + Outcome + Interest */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {sentiment && (
          <div className="p-3 rounded-lg bg-white border border-gray-200">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Sentiment</p>
            <SentimentBadge sentiment={sentiment} />
          </div>
        )}
        {outcome && (
          <div className="p-3 rounded-lg bg-white border border-gray-200">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Outcome</p>
            <div className="flex items-center gap-2">
              {(outcome === 'hot_lead' || outcome === 'site_visit_scheduled') ? (
                <CheckCircle className="w-4 h-4 text-emerald-400" />
              ) : (outcome === 'not_interested' || outcome === 'wrong_number') ? (
                <AlertCircle className="w-4 h-4 text-rose-400" />
              ) : (
                <Target className="w-4 h-4 text-amber-400" />
              )}
              <span className="text-sm font-medium text-gray-900 capitalize">{(typeof outcome === 'string' ? outcome : JSON.stringify(outcome)).replace(/_/g, ' ')}</span>
            </div>
          </div>
        )}
        {effectiveInterest != null && (
          <div className="p-3 rounded-lg bg-white border border-gray-200">
            <InterestLevel level={effectiveInterest} />
          </div>
        )}
      </div>

      {/* Summary */}
      {summary && (
        <div className="p-3 rounded-lg bg-white border border-gray-200">
          <div className="flex items-center gap-2 mb-2">
            <MessageSquare className="w-3.5 h-3.5 text-gray-500" />
            <p className="text-xs text-gray-500 uppercase tracking-wider">Summary</p>
          </div>
          <p className="text-sm text-gray-700 leading-relaxed">
            {typeof summary === 'object' ? JSON.stringify(summary) : String(summary)}
          </p>
        </div>
      )}

      {/* Lists */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {key_topics.length > 0 && (
          <div className="p-3 rounded-lg bg-white border border-gray-200">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Key Topics</p>
            <div className="flex flex-wrap gap-1.5">
              {key_topics.map((item, i) => (
                <Badge key={i} variant="outline" className="bg-gray-100 text-gray-600 border-gray-300 text-xs">
                  {typeof item === 'object' ? JSON.stringify(item) : String(item)}
                </Badge>
              ))}
            </div>
          </div>
        )}
        {key_objections.length > 0 && (
          <div className="p-3 rounded-lg bg-white border border-gray-200">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Key Objections</p>
            <div className="flex flex-wrap gap-1.5">
              {key_objections.map((item, i) => (
                <Badge key={i} variant="outline" className="bg-rose-50 text-rose-600 border-rose-200 text-xs">
                  {typeof item === 'object' ? JSON.stringify(item) : String(item)}
                </Badge>
              ))}
            </div>
          </div>
        )}
        {action_items.length > 0 && (
          <div className="p-3 rounded-lg bg-white border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <ListChecks className="w-3.5 h-3.5 text-gray-500" />
              <p className="text-xs text-gray-500 uppercase tracking-wider">Action Items</p>
            </div>
            <ul className="space-y-1">
              {action_items.map((item, i) => (
                <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                  <span className="text-blue-400 mt-0.5">-</span>
                  {typeof item === 'object' ? JSON.stringify(item) : String(item)}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Intent & Next Action */}
      {(intentStr || nextActionStr) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {intentStr && (
            <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Prospect Intent</p>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${
                intentStr.toLowerCase().includes('interested') && !intentStr.toLowerCase().includes('not interested') ? 'bg-emerald-100 text-emerald-700 border-emerald-200' :
                intentStr.toLowerCase().includes('not interested') || intentStr.toLowerCase().includes('already bought') ? 'bg-rose-100 text-rose-700 border-rose-200' :
                intentStr.toLowerCase().includes('site visit') ? 'bg-blue-100 text-blue-700 border-blue-200' :
                intentStr.toLowerCase().includes('negotiation') || intentStr.toLowerCase().includes('callback') ? 'bg-amber-100 text-amber-700 border-amber-200' :
                'bg-gray-100 text-gray-600 border-gray-200'
              }`}>{intentStr}</span>
            </div>
          )}
          {nextActionStr && (
            <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Next Action</p>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${
                nextActionStr.toLowerCase().includes('site visit') ? 'bg-blue-100 text-blue-700 border-blue-200' :
                nextActionStr.toLowerCase().includes('callback') || nextActionStr.toLowerCase().includes('call') ? 'bg-amber-100 text-amber-700 border-amber-200' :
                nextActionStr.toLowerCase().includes('whatsapp') || nextActionStr.toLowerCase().includes('send') ? 'bg-green-100 text-green-700 border-green-200' :
                nextActionStr.toLowerCase().includes('no action') ? 'bg-rose-100 text-rose-700 border-rose-200' :
                'bg-gray-100 text-gray-600 border-gray-200'
              }`}>{nextActionStr}</span>
            </div>
          )}
        </div>
      )}

      {/* Extra fields */}
      {Object.keys(otherFields).length > 0 && (
        <div className="p-3 rounded-lg bg-white border border-gray-200">
          <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Additional Data</p>
          <div className="space-y-2">
            {Object.entries(otherFields).map(([key, value]) => (
              <div key={key} className="flex justify-between items-start">
                <span className="text-xs text-gray-500 capitalize">{key.replace(/_/g, ' ')}</span>
                <span className="text-sm text-gray-700 text-right max-w-[60%]">
                  {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
